<device-list>
  <div>
    <!-- Page Header con botón de refrescar personalizado -->
    <page-header title="Dispositivos" subtitle="Gestiona tus dispositivos conectados">
      <button slot="actions" class="btn btn-outline-primary" onclick={ handleRefresh } disabled={ state.refreshing }>
        <i class="bi bi-arrow-clockwise me-1" class={ { 'spin' : state.refreshing } }></i>
        { state.refreshing ? 'Actualizando...' : 'Refrescar' }
      </button>
    </page-header>


    <!-- Device Table Component - only render when we have data -->
    <device-table
      if={ state.dataReady }
      devices={ state.enrichedDevices }
      isAdmin={ props.isAdmin }
      onviewdetails={ handleViewDetails }
      oneditschedule={ handleEditSchedule }
      ondevicesupdated={ handleDevicesUpdated }
    ></device-table>

    <!-- Loading state -->
    <loading-spinner if={ !state.dataReady } message="Cargando dispositivos..." />

    <!-- Componente para editar schedule -->
    <schedule-editor
      id="scheduleEditorComponent"
      onschedulesaved={ handleScheduleSaved }
      deviceId={ state.scheduleData.deviceId }
      deviceName={ state.scheduleData.deviceName }
      channelId={ state.scheduleData.channelId }
      channelName={ state.scheduleData.channelName }
      schedule={ state.scheduleData.schedule }
    ></schedule-editor>

  </div>

  <script>
    import DeviceTable from './device-table.riot'
    import ScheduleEditor from './schedule-editor.riot'
    import PageHeader from './page-header.riot'
    import LoadingSpinner from './loading-spinner.riot'
    import { AuthService } from '../services'
    import { ToastService } from '../services/toast-service'
    import { webSocketService } from '../services/WebSocketService'
    import { enrichDevicesWithUserNames } from '../utils/device-utils'

    export default {
      components: {
        DeviceTable,
        ScheduleEditor,
        PageHeader,
        LoadingSpinner
      },

      scheduleEditorEl: null,
      _wsMessageHandler: null,

      onBeforeMount() {
        this.state = {
          refreshing: false,
          enrichedDevices: [],
          dataReady: false,
          wsListenersAttached: false,
          scheduleData: {
            deviceId: '',
            deviceName: '',
            channelId: '',
            channelName: '',
            schedule: null
          }
        }
      },

      async onMounted() {
        // Obtener referencia al componente schedule-editor
        this.scheduleEditorEl = document.getElementById('scheduleEditorComponent')

        // Suscribirse al WebSocket para actualizaciones en tiempo real
        if (!this.state.wsListenersAttached) {
          this._wsMessageHandler = this.handleWebSocketMessage.bind(this)
          console.log('[DeviceList] Adding WebSocket listener...')
          webSocketService.on('messageReceived', this._wsMessageHandler)
          this.update({ wsListenersAttached: true })
        }

        // Enriquecer dispositivos con nombres de usuario (si ya están cargados)
        if (this.props.devices && this.props.devices.length > 0) {
          await this.enrichDevices()
        }
      },

      onBeforeUpdate(newProps, newState) {
        // Check if devices prop changed - compare array lengths and content
        if (newProps.devices && newProps.devices !== this.props.devices) {
          this.devicesPropChanged = true;
          console.log('[DeviceList] Devices prop changed, will re-enrich');
        }
      },

      async onUpdated(prevProps, prevState) {
        // If devices prop changed, enrich them
        if (this.devicesPropChanged) {
          this.devicesPropChanged = false;
          console.log('[DeviceList] Props changed, enriching devices...');
          await this.enrichDevices();
        }
      },

      onBeforeUnmount() {
        if (this._wsMessageHandler) {
          console.log('[DeviceList] Removing WebSocket listener...')
          webSocketService.off('messageReceived', this._wsMessageHandler)
        }
        this.update({ wsListenersAttached: false })
        this._wsMessageHandler = null
      },

      handleWebSocketMessage({ type, payload }) {
        // Solo procesar eventos de última conexión para actualizar el estado online/offline
        const payloadDeviceId = payload?.deviceId || (payload?.app && payload?.city && payload?.name
          ? `${payload.app}/${payload.city}/${payload.name}`
          : null)

        if (type === 'deviceLastConnection' && payloadDeviceId) {
          console.log('[DeviceList] Received deviceLastConnection:', payload)
          this.updateDeviceLastConnection(payloadDeviceId, payload.date || payload.lastConnection)
        }
      },

      updateDeviceLastConnection(deviceId, dateString) {
        if (!this.state.enrichedDevices || this.state.enrichedDevices.length === 0) {
          return
        }

        const newTimestamp = Date.parse(dateString)
        if (isNaN(newTimestamp)) {
          console.warn('[DeviceList] Received invalid date format:', dateString)
          return
        }

        // Actualizar el dispositivo en la lista
        const updatedDevices = this.state.enrichedDevices.map(device => {
          const currentDeviceId = `${device.app}/${device.city}/${device.name}`
          if (currentDeviceId === deviceId) {
            console.log(`[DeviceList] Updating lastConnection for ${deviceId}:`, newTimestamp)
            return { ...device, lastConnection: newTimestamp }
          }
          return device
        })

        // Solo actualizar si realmente cambió algo
        const hasChanged = updatedDevices.some((device, index) =>
          device.lastConnection !== this.state.enrichedDevices[index].lastConnection
        )

        if (hasChanged) {
          this.update({ enrichedDevices: [...updatedDevices] })
        }
      },

      async enrichDevices() {
        if (!this.props.devices || this.props.devices.length === 0) {
          this.update({ enrichedDevices: [], dataReady: true })
          return
        }

        try {
          let enrichedDevices;

          // Only enrich with user names if user is admin
          if (this.props.isAdmin) {
            enrichedDevices = await enrichDevicesWithUserNames(this.props.devices)
          } else {
            enrichedDevices = this.props.devices
          }

          // Normalize lastConnection to number (in case it comes as string)
          const normalizedDevices = enrichedDevices.map(device => ({
            ...device,
            lastConnection: typeof device.lastConnection === 'string'
              ? new Date(device.lastConnection).getTime()
              : device.lastConnection
          }));

          // Force update to trigger child component re-render and mark data as ready
          this.update({ enrichedDevices: [...normalizedDevices], dataReady: true })
        } catch (error) {
          console.error('Error enriching devices:', error)
          // Fallback to original devices if enrichment fails
          const normalizedDevices = this.props.devices.map(device => ({
            ...device,
            lastConnection: typeof device.lastConnection === 'string'
              ? new Date(device.lastConnection).getTime()
              : device.lastConnection
          }));
          this.update({ enrichedDevices: [...normalizedDevices], dataReady: true })
        }
      },

      async handleRefresh() {
        if (this.props.onrefreshdevices) {
          this.update({
            refreshing: true,
            dataReady: false // Marcar como no listo temporalmente
          });

          try {
            await this.props.onrefreshdevices();

            // Esperar a que las props se actualicen y luego enriquecer
            await this.enrichDevices();

            this.update({ refreshing: false });
            ToastService.success('Dispositivos actualizados correctamente');
          } catch (error) {
            console.error('Error refreshing devices:', error);
            this.update({ refreshing: false, dataReady: true });
            ToastService.error('Error al actualizar dispositivos');
          }
        }
      },

      handleEditSchedule(deviceId, deviceName, channel) {
        this.update({
          scheduleData: {
            deviceId,
            deviceName,
            channelId: channel.channel,
            channelName: channel.name,
            schedule: channel.schedule
          }
        });

        const scheduleEditor = this.scheduleEditorEl;

        if (scheduleEditor && scheduleEditor._tag && typeof scheduleEditor._tag.open === 'function') {
          scheduleEditor._tag.open(
            deviceId,
            deviceName,
            channel.channel,
            channel.name,
            channel.schedule
          );
        }
      },

      handleScheduleSaved(data) {
        if (this.props.ondevicesupdated) {
          this.props.ondevicesupdated();
        } else {
          this.props.onrefreshdevices && this.props.onrefreshdevices();
        }

        ToastService.success('Horario actualizado correctamente');
      },

      handleDevicesUpdated() {
        if (this.props.ondevicesupdated) {
          this.props.ondevicesupdated();
        }
      },

      handleViewDetails(deviceId) {
        if (this.props.onviewdetails) {
          this.props.onviewdetails(deviceId);
        }
      }
    }
  </script>

</device-list>
